/**
 * /job/{ticketNumber} — the mobile job view. Where a scanned job label lands
 * while the work is under way (docs/qr-scan-workflow-requirements.md §9, D14):
 * tick the checklist (the page opens at the next open task), change the
 * status, add a note or a photo (D15), and release the device to the customer while the job
 * waits for parts (D17). Everything else — labour, parts, attaching a task
 * list — is the desktop page, one tap away as "Full view".
 *
 * Thin over the shared hooks (D7): the checklist is `useWorkItemChecklist` +
 * `ChecklistPanel`, the status change is the same `updateWorkItemStatus` +
 * `watchJobStatusResult` pair the dashboard uses, notes are job logs.
 * Mounted outside MainLayout, like /pickup.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import type { SavedAudit, SavedChecklist } from '@techgitter/contracts';

import type { WorkItem } from '../components/workitems/workItemTypes';
import { ChecklistPanel } from '../components/tickets/ChecklistPanel';
import {
  STATUS_PICKER_OPTIONS,
  statusDropdownDvValue,
  toStatusDropdownValue,
} from '../components/technician-card/statusPickerOptions';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { formatStatusDisplay, normalizeStatus } from '../components/workitems/utils/workItemUtils';
import { useJobLogs } from '../hooks/useJobLogs';
import { useSignalREvent } from '../hooks/useSignalR';
import { useTaskDropdowns } from '../hooks/useTaskDropdowns';
import { useWorkItemChecklist } from '../hooks/useWorkItemChecklist';
import { watchJobStatusResult } from '../services/jobStatusWatcher';
import { returnWorkItemDevice, updateWorkItemStatus } from '../services/workItemDataService';
import { useWorkItemByNumber } from '../hooks/useWorkItemByNumber';
import { formatSydneyDateTime } from '../utils/sydneyTime';
import { findNextOpenTask } from '../utils/checklistEngine';
import { canReleaseDevice, canReturnDevice, deviceReleaseSummary } from '../utils/deviceRelease';
import { ReleaseDeviceDialog } from '../components/workitems/deviceRelease/ReleaseDeviceDialog';
import { JobPhotosSection } from '../components/workitems/JobPhotosSection';
import './MobilePickupPage.css';
import './MobileJobPage.css';

const STATUS_CHOICES = STATUS_PICKER_OPTIONS.filter((o) => o.value !== 'deleted');
const RECENT_NOTES = 5;

export function MobileJobPage(): React.ReactElement {
  const { ticketNumber = '' } = useParams<{ ticketNumber: string }>();
  const { item, setItem, loading, error } = useWorkItemByNumber(ticketNumber);

  // Someone else changed this job (status, customer, collected) — take the
  // fresh copy. The checklist keeps its own in-progress edits regardless: the
  // hook hydrates once.
  useSignalREvent('workitems:changed', (data: unknown) => {
    const payload = data as { action?: string; workItem?: WorkItem } | undefined;
    const incoming = payload?.workItem;
    if (!incoming || !item || incoming.guid !== item.guid) return;
    setItem((prev) => (prev ? { ...prev, ...incoming } : incoming));
  });

  if (loading) {
    return (
      <div className="pickup-page pickup-center">
        <CircularProgress />
      </div>
    );
  }
  if (error || !item) {
    return (
      <div className="pickup-page">
        <Alert severity="error">{error ?? 'Could not load this job.'}</Alert>
      </div>
    );
  }
  return <MobileJobBody key={item.guid} item={item} onItemChange={setItem} />;
}

interface BodyProps {
  item: WorkItem;
  onItemChange: (update: (prev: WorkItem | null) => WorkItem | null) => void;
}

function MobileJobBody({ item, onItemChange }: BodyProps): React.ReactElement {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const userName = currentUser?.displayName || currentUser?.email || '';
  const jobGuid = item.jobGUID ?? item.guid;

  // ---- checklist -----------------------------------------------------------
  const { dropdownCatalog } = useTaskDropdowns();
  const handleSaved = useCallback(
    (taskList: SavedChecklist, taskAudit: SavedAudit) => {
      onItemChange((prev) => (prev ? { ...prev, taskList, taskAudit } : prev));
    },
    [onItemChange],
  );
  const cl = useWorkItemChecklist({
    jobGuid,
    savedChecklist: item.taskList,
    savedAudit: item.taskAudit,
    userName,
    taskDropdowns: dropdownCatalog.dropdowns,
    onSaved: handleSaved,
  });
  const hasChecklist = !!cl.checklist && cl.checklist.sections.length > 0;

  // Open at the next thing to do — once, on arrival, not on every tick.
  const scrolled = useRef(false);
  useEffect(() => {
    if (scrolled.current || !cl.checklist) return;
    scrolled.current = true;
    const next = findNextOpenTask(cl.checklist);
    if (!next) return;
    const el = document.querySelector(`[data-task-id="${CSS.escape(next.taskId)}"]`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [cl.checklist]);

  // ---- status --------------------------------------------------------------
  const currentStatus = item.jobStatus || item.status || '';
  const [statusValue, setStatusValue] = useState(toStatusDropdownValue(currentStatus));
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusNotice, setStatusNotice] = useState<string | null>(null);

  useEffect(() => {
    setStatusValue(toStatusDropdownValue(currentStatus));
  }, [currentStatus]);

  const target = statusDropdownDvValue(statusValue);
  // The work item carries a slug (`in-progress`); the picker hands back the
  // RepairShopr label — compare both as slugs.
  const unchanged = !target || normalizeStatus(target) === normalizeStatus(currentStatus);
  const blockedByTasks = !!target && normalizeStatus(target) === 'resolved' && cl.progress.hasBlockingTasks;

  const handleStatus = async () => {
    if (!target || unchanged || blockedByTasks) return;
    setStatusBusy(true);
    setStatusError(null);
    setStatusNotice(null);
    const previous = currentStatus;
    onItemChange((prev) => (prev ? { ...prev, status: target, jobStatus: target } : prev));
    const revert = (reason: string) => {
      onItemChange((prev) => (prev ? { ...prev, status: previous, jobStatus: previous } : prev));
      setStatusError(`Couldn't set “${target}”: ${reason}`);
    };
    try {
      const { messageId } = await updateWorkItemStatus(
        item.guid, item.id || item.guid, target, item.type,
      );
      setStatusNotice(`Status set to ${target}.`);
      if (messageId) {
        // 202 only means queued — RepairShopr can still refuse it later.
        watchJobStatusResult(messageId, {
          onFailure: (reason) => {
            setStatusNotice(null);
            revert(reason);
          },
        });
      }
    } catch (e) {
      setStatusNotice(null);
      revert(e instanceof Error ? e.message : 'request failed');
    } finally {
      setStatusBusy(false);
    }
  };

  // ---- device release ------------------------------------------------------
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [returning, setReturning] = useState(false);
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const handleReturned = async () => {
    setReturning(true);
    setReleaseError(null);
    try {
      const result = await returnWorkItemDevice(item.guid);
      onItemChange((prev) =>
        prev ? { ...prev, deviceWithCustomer: false, deviceReturnedAt: result.deviceReturnedAt ?? undefined } : prev,
      );
    } catch (e) {
      setReleaseError(e instanceof Error ? e.message : 'Failed to mark device returned');
    } finally {
      setReturning(false);
    }
  };

  // ---- notes ---------------------------------------------------------------
  const { logs, isSubmitting, error: logError, addLog } = useJobLogs(jobGuid, userName);
  const [noteText, setNoteText] = useState('');
  const recentLogs = [...logs]
    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
    .slice(0, RECENT_NOTES);

  const handleAddNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    // Keep the text if the save failed — a note typed on flaky phone data
    // must not vanish with the error.
    if (await addLog({ type: 'comment', text })) setNoteText('');
  };

  const customer = item.customerBusinessName || item.customerName || 'Customer';
  const waitingOnPayment = normalizeStatus(currentStatus) === 'waiting-on-payment';

  return (
    <div className="pickup-page mobile-job">
      <header className="pickup-header">
        <div className="pickup-ticket">#{item.number}</div>
        <div className="pickup-customer">{customer}</div>
        {item.title && <div className="pickup-muted">{item.title}</div>}
        {currentStatus && <span className="pickup-status">{formatStatusDisplay(currentStatus)}</span>}
      </header>

      <Stack spacing={2}>
        {waitingOnPayment && (
          <Alert severity="warning">
            Waiting on payment — don't start diagnostics until the invoice is paid.
          </Alert>
        )}

        <section className="pickup-card">
          <div className="mobile-job-heading">
            Checklist
            {hasChecklist && (
              <span className="pickup-muted">
                {cl.progress.closed} of {cl.progress.total} done
              </span>
            )}
          </div>
          {hasChecklist && cl.checklist ? (
            <div className="mobile-job-checklist">
              <ChecklistPanel
                checklist={cl.checklist}
                progress={cl.progress}
                disabled={cl.isSaving}
                taskDropdowns={dropdownCatalog.dropdowns}
                onSetStatus={cl.onSetStatus}
                onSetNote={cl.onSetNote}
                onSetOption={cl.onSetOption}
                onRemoveTask={cl.onRemoveTask}
                onRestoreTask={cl.onRestoreTask}
                onRemoveSection={cl.onRemoveSection}
                onRestoreSection={cl.onRestoreSection}
                onAddManualTask={cl.onAddManualTask}
              />
            </div>
          ) : (
            <p className="pickup-muted">No checklist on this job. Attach one from the full view.</p>
          )}
          {cl.saveError && <Alert severity="error" sx={{ mt: 1 }}>{cl.saveError}</Alert>}
        </section>

        <section className="pickup-card">
          <div className="mobile-job-heading">Status</div>
          <FormControl fullWidth disabled={statusBusy} sx={{ mb: 2 }}>
            <InputLabel id="mj-status">Status</InputLabel>
            <Select
              labelId="mj-status"
              label="Status"
              value={statusValue}
              onChange={(e) => setStatusValue(String(e.target.value))}
            >
              {STATUS_CHOICES.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {blockedByTasks && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              All tasks must be actioned before resolving.
            </Alert>
          )}
          {cl.isDirty && !unchanged && (
            <Alert severity="info" sx={{ mb: 2 }}>Save the checklist first.</Alert>
          )}
          {statusError && <Alert severity="error" sx={{ mb: 2 }}>{statusError}</Alert>}
          {statusNotice && <Alert severity="success" sx={{ mb: 2 }}>{statusNotice}</Alert>}
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleStatus}
            disabled={unchanged || blockedByTasks || statusBusy || cl.isDirty}
            startIcon={statusBusy ? <CircularProgress size={18} /> : null}
          >
            {statusBusy ? 'Updating…' : 'Update status'}
          </Button>
        </section>

        {(canReleaseDevice(item) || canReturnDevice(item)) && (
          <section className="pickup-card">
            <div className="mobile-job-heading">Device</div>
            {canReturnDevice(item) ? (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  With the customer — {deviceReleaseSummary(item)}
                </Alert>
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  onClick={handleReturned}
                  disabled={returning}
                  startIcon={returning ? <CircularProgress size={18} /> : null}
                >
                  {returning ? 'Marking…' : 'Mark device returned'}
                </Button>
              </>
            ) : (
              <Button variant="outlined" size="large" fullWidth onClick={() => setReleaseOpen(true)}>
                Release device to customer
              </Button>
            )}
            {releaseError && <Alert severity="error" sx={{ mt: 2 }}>{releaseError}</Alert>}
          </section>
        )}

        <section className="pickup-card">
          <div className="mobile-job-heading">Photos</div>
          <JobPhotosSection jobGuid={jobGuid} />
        </section>

        <section className="pickup-card">
          <div className="mobile-job-heading">Notes</div>
          <TextField
            label="Add a note"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            disabled={isSubmitting}
            sx={{ mb: 1 }}
          />
          <Button
            variant="outlined"
            fullWidth
            onClick={handleAddNote}
            disabled={!noteText.trim() || isSubmitting}
          >
            {isSubmitting ? 'Adding…' : 'Add note'}
          </Button>
          {logError && <Alert severity="error" sx={{ mt: 1 }}>{logError}</Alert>}
          {recentLogs.length > 0 && (
            <ul className="mobile-job-logs">
              {recentLogs.map((log) => (
                <li key={log.guid || log.id}>
                  <div className="pickup-muted">
                    {log.createdBy} · {formatSydneyDateTime(log.createdAt, { withYear: false })}
                  </div>
                  <div className="mobile-job-log-text">{log.text}</div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <Button variant="outlined" size="large" onClick={() => navigate(`/ticket/${item.number}`)}>
          Full view
        </Button>
      </Stack>

      <ReleaseDeviceDialog
        open={releaseOpen}
        workItem={item}
        onClose={() => setReleaseOpen(false)}
        onReleased={(updates) => onItemChange((prev) => (prev ? { ...prev, ...updates } : prev))}
      />

      {cl.isDirty && (
        <div className="mobile-job-savebar">
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={cl.onSave}
            disabled={cl.isSaving}
            startIcon={cl.isSaving ? <CircularProgress size={18} /> : null}
          >
            {cl.isSaving ? 'Saving…' : 'Save checklist'}
          </Button>
        </div>
      )}
    </div>
  );
}

export default MobileJobPage;
